import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { ScrollArea } from '../ui/scroll-area';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { toast } from 'sonner';
import {
  RefreshCw,
  Download,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  GitBranch,
  Server,
  Database,
  Package,
  Code,
  RotateCcw,
  Loader2,
  History,
  Info,
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Update steps for progress display
const UPDATE_STEPS = [
  { id: 1, name: 'Validasi & Lock', icon: CheckCircle2 },
  { id: 2, name: 'Backup Database', icon: Database },
  { id: 3, name: 'Git Fetch', icon: GitBranch },
  { id: 4, name: 'Git Reset', icon: RotateCcw },
  { id: 5, name: 'Install Backend Dependencies', icon: Package },
  { id: 6, name: 'Install Frontend Dependencies', icon: Package },
  { id: 7, name: 'Build Frontend', icon: Code },
  { id: 8, name: 'Restart Backend Service', icon: Server },
];

export const UpdateSettings = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [updateInfo, setUpdateInfo] = useState(null);
  const [updateStatus, setUpdateStatus] = useState(null);
  const [updateLogs, setUpdateLogs] = useState([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pollingInterval, setPollingInterval] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token');
    return { Authorization: `Bearer ${token}` };
  };

  // Check for updates
  const checkForUpdates = async (showToast = true) => {
    setChecking(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/update/check`, {
        headers: getAuthHeaders(),
      });
      setUpdateInfo(response.data);
      
      if (showToast) {
        if (response.data.update_available) {
          toast.info(`Update tersedia: ${response.data.commits_behind} commit baru`);
        } else {
          toast.success('Aplikasi sudah versi terbaru');
        }
      }
    } catch (error) {
      console.error('Check update error:', error);
      if (showToast) {
        toast.error(error.response?.data?.detail || 'Gagal memeriksa update');
      }
    } finally {
      setChecking(false);
    }
  };

  // Get update status
  const getUpdateStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/update/status`, {
        headers: getAuthHeaders(),
      });
      setUpdateStatus(response.data);
      
      // Stop polling if update completed
      if (response.data.status === 'SUCCESS') {
        stopPolling();
        toast.success('Update berhasil! Halaman akan dimuat ulang...');
        setTimeout(() => window.location.reload(), 3000);
      } else if (response.data.status === 'FAILED') {
        stopPolling();
        toast.error(`Update gagal: ${response.data.error_message}`);
      }
      
      return response.data;
    } catch (error) {
      console.error('Get status error:', error);
      return null;
    }
  }, []);

  // Get update logs
  const getUpdateLogs = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/update/logs`, {
        headers: getAuthHeaders(),
      });
      setUpdateLogs(response.data.logs);
    } catch (error) {
      console.error('Get logs error:', error);
    }
  };

  // Start update
  const startUpdate = async () => {
    setShowConfirmDialog(false);
    setLoading(true);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/update/run`,
        {},
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        toast.info('Update dimulai...');
        startPolling();
      }
    } catch (error) {
      console.error('Start update error:', error);
      toast.error(error.response?.data?.detail || 'Gagal memulai update');
      setLoading(false);
    }
  };

  // Polling for status
  const startPolling = () => {
    if (pollingInterval) return;
    
    const interval = setInterval(async () => {
      const status = await getUpdateStatus();
      if (status && (status.status === 'SUCCESS' || status.status === 'FAILED' || status.status === 'IDLE')) {
        setLoading(false);
      }
    }, 2000);
    
    setPollingInterval(interval);
  };

  const stopPolling = () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  };

  // Initial load
  useEffect(() => {
    checkForUpdates(false); // Don't show toast on initial load
    getUpdateStatus();
    getUpdateLogs();
    
    return () => stopPolling();
  }, []);

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('id-ID', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  const isUpdating = updateStatus?.status === 'RUNNING' || loading;

  return (
    <div className="space-y-6">
      {/* Version Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <GitBranch className="h-5 w-5" />
            Informasi Versi
          </CardTitle>
          <CardDescription>
            Status versi aplikasi saat ini dan pembaruan yang tersedia
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Current Version */}
            <div className="p-4 rounded-lg border bg-card">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                <Server className="h-4 w-4" />
                Versi Saat Ini
              </div>
              <div className="text-2xl font-mono font-bold">
                {updateInfo?.current_version || '...'}
              </div>
              {updateInfo?.current_commit_date && (
                <div className="text-xs text-muted-foreground mt-1">
                  {formatDate(updateInfo.current_commit_date)}
                </div>
              )}
            </div>

            {/* Latest Version */}
            <div className="p-4 rounded-lg border bg-card">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                <Download className="h-4 w-4" />
                Versi Terbaru
              </div>
              <div className="text-2xl font-mono font-bold">
                {updateInfo?.latest_version || '...'}
              </div>
              {updateInfo?.latest_commit_date && (
                <div className="text-xs text-muted-foreground mt-1">
                  {formatDate(updateInfo.latest_commit_date)}
                </div>
              )}
            </div>
          </div>

          {/* Update Status Badge */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {updateInfo?.update_available ? (
                <Badge variant="destructive" className="gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  Update Tersedia ({updateInfo.commits_behind} commit)
                </Badge>
              ) : updateInfo ? (
                <Badge variant="default" className="gap-1 bg-green-600">
                  <CheckCircle2 className="h-3 w-3" />
                  Sudah Terbaru
                </Badge>
              ) : null}
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={checkForUpdates}
                disabled={checking || isUpdating}
              >
                {checking ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Cek Update
              </Button>

              <Button
                size="sm"
                onClick={() => setShowConfirmDialog(true)}
                disabled={!updateInfo?.update_available || isUpdating}
              >
                {isUpdating ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Update Sekarang
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Update Progress Card - Show when updating */}
      {(isUpdating || updateStatus?.status === 'FAILED') && (
        <Card className={updateStatus?.status === 'FAILED' ? 'border-destructive' : ''}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {updateStatus?.status === 'FAILED' ? (
                <XCircle className="h-5 w-5 text-destructive" />
              ) : (
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              )}
              {updateStatus?.status === 'FAILED' ? 'Update Gagal' : 'Proses Update'}
            </CardTitle>
            <CardDescription>
              {updateStatus?.status === 'FAILED'
                ? 'Terjadi kesalahan saat proses update'
                : 'Jangan tutup halaman ini selama proses update berlangsung'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Progress Bar */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>{updateStatus?.step_name || 'Memulai...'}</span>
                <span>{updateStatus?.progress || 0}%</span>
              </div>
              <Progress value={updateStatus?.progress || 0} className="h-2" />
            </div>

            {/* Steps */}
            <div className="space-y-2">
              {UPDATE_STEPS.map((step) => {
                const StepIcon = step.icon;
                const currentStep = updateStatus?.current_step || 0;
                const isCompleted = step.id < currentStep;
                const isCurrent = step.id === currentStep;
                const isFailed = updateStatus?.status === 'FAILED' && isCurrent;

                return (
                  <div
                    key={step.id}
                    className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${
                      isCurrent
                        ? isFailed
                          ? 'bg-destructive/10 text-destructive'
                          : 'bg-primary/10 text-primary'
                        : isCompleted
                        ? 'text-green-600'
                        : 'text-muted-foreground'
                    }`}
                  >
                    <div className="w-6 h-6 flex items-center justify-center">
                      {isCompleted ? (
                        <CheckCircle2 className="h-5 w-5" />
                      ) : isCurrent ? (
                        isFailed ? (
                          <XCircle className="h-5 w-5" />
                        ) : (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        )
                      ) : (
                        <div className="w-2 h-2 rounded-full bg-current opacity-30" />
                      )}
                    </div>
                    <span className="text-sm">{step.name}</span>
                  </div>
                );
              })}
            </div>

            {/* Error Message */}
            {updateStatus?.error_message && (
              <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                <div className="font-medium mb-1">Error:</div>
                <div className="font-mono text-xs">{updateStatus.error_message}</div>
              </div>
            )}

            {/* Backup Info */}
            {updateStatus?.backup_id && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Database className="h-4 w-4" />
                <span>Backup ID: {updateStatus.backup_id.slice(0, 8)}...</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Warning Info */}
      <Card className="border-yellow-500/50 bg-yellow-500/5">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <Info className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div className="space-y-2 text-sm">
              <p className="font-medium text-yellow-500">Informasi Penting:</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>Database akan di-backup otomatis sebelum update</li>
                <li>Data Anda tidak akan hilang selama proses update</li>
                <li>Jika update gagal, database backup tersimpan aman</li>
                <li>Aplikasi mungkin tidak responsif selama 1-5 menit</li>
                <li>Jangan tutup browser selama proses berlangsung</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Update History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Riwayat Update
          </CardTitle>
          <CardDescription>
            Log aktivitas update sistem
          </CardDescription>
        </CardHeader>
        <CardContent>
          {updateLogs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <History className="h-12 w-12 mx-auto mb-2 opacity-20" />
              <p>Belum ada riwayat update</p>
            </div>
          ) : (
            <ScrollArea className="h-[300px]">
              <div className="space-y-3">
                {updateLogs.map((log) => (
                  <div
                    key={log.id}
                    className="flex items-start gap-3 p-3 rounded-lg border bg-card"
                  >
                    <div className="mt-0.5">
                      {log.status === 'SUCCESS' ? (
                        <CheckCircle2 className="h-5 w-5 text-green-500" />
                      ) : log.status === 'FAILED' ? (
                        <XCircle className="h-5 w-5 text-destructive" />
                      ) : (
                        <Clock className="h-5 w-5 text-yellow-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge
                          variant={log.status === 'SUCCESS' ? 'default' : 'destructive'}
                          className={log.status === 'SUCCESS' ? 'bg-green-600' : ''}
                        >
                          {log.status}
                        </Badge>
                        {log.old_version && log.new_version && (
                          <span className="text-xs font-mono text-muted-foreground">
                            {log.old_version} → {log.new_version}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">
                        {formatDate(log.started_at)} oleh {log.triggered_by_name}
                      </div>
                      {log.error_message && (
                        <div className="text-xs text-destructive mt-1 truncate">
                          {log.error_message}
                        </div>
                      )}
                      {log.backup_id && (
                        <div className="text-xs text-muted-foreground mt-1">
                          Backup: {log.backup_id.slice(0, 8)}...
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              Konfirmasi Update Sistem
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                Sistem akan diperbarui ke versi terbaru dari repository GitHub.
                Proses ini akan:
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Membuat backup database secara otomatis</li>
                <li>Mengunduh kode terbaru dari GitHub</li>
                <li>Menginstall dependencies yang diperlukan</li>
                <li>Me-rebuild aplikasi frontend</li>
                <li>Me-restart backend service</li>
              </ul>
              <p className="font-medium text-yellow-600">
                Jangan tutup halaman ini selama proses update berlangsung!
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={startUpdate}>
              Ya, Update Sekarang
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UpdateSettings;
